import { useState, type JSX } from "react";

import { useVoss } from "../state/VossProvider";
import { Button } from "./primitives";
import { IconLock, IconPlus } from "./icons";

const glyph = (kind: string): string => kind.charAt(0).toUpperCase();

/** Left rail: brand, New Chat, workspace switcher, and searchable conversation history. */
export function Sidebar({ open, onCloseMobile }: { open: boolean; onCloseMobile: () => void }): JSX.Element {
  const { workspaces, currentWorkspaceId, selectWorkspace, conversations, activeConversation, selectConversation, newChat } = useVoss();
  const [query, setQuery] = useState("");

  const pick = (fn: () => void): void => {
    fn();
    onCloseMobile();
  };

  const withMessages = conversations.filter((c) => c.messages.length > 0);
  const q = query.trim().toLowerCase();
  const filtered = q ? withMessages.filter((c) => c.title.toLowerCase().includes(q)) : withMessages;

  return (
    <aside className="voss-sidebar" data-open={open} aria-label="Workspaces and conversations">
      <div className="voss-brand">
        <div className="voss-brand__mark" aria-hidden />
        <div>
          <div className="voss-brand__name">VOSS</div>
          <div className="voss-brand__tag">Intelligence OS</div>
        </div>
      </div>

      <Button variant="primary" onClick={() => pick(newChat)} aria-keyshortcuts="Meta+Shift+K">
        <IconPlus width={16} height={16} /> New chat
      </Button>

      <nav aria-label="Workspaces">
        <div className="voss-section-label">Workspaces</div>
        <div className="voss-ws-list">
          {workspaces.map((w) => (
            <button key={w.id} type="button" className="voss-ws" aria-current={w.id === currentWorkspaceId} onClick={() => pick(() => selectWorkspace(w.id))}>
              <span className="voss-ws__glyph" aria-hidden>
                {glyph(w.kind)}
              </span>
              <span className="voss-ws__label">{w.label}</span>
              {w.localOnly ? (
                <span className="voss-ws__lock" title="Local-only workspace">
                  <IconLock />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </nav>

      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <div className="voss-section-label">History</div>
        {withMessages.length > 0 ? (
          <input className="voss-search" placeholder="Search conversations" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search conversations" />
        ) : null}
        <div className="voss-history">
          {withMessages.length === 0 ? (
            <p className="voss-empty">No conversations yet</p>
          ) : filtered.length === 0 ? (
            <p className="voss-empty">No matches</p>
          ) : (
            filtered.map((c) => (
              <button key={c.id} type="button" className="voss-history__item" aria-current={c.id === activeConversation?.id} onClick={() => pick(() => selectConversation(c.id))}>
                {c.title}
              </button>
            ))
          )}
        </div>
      </div>
    </aside>
  );
}
